import {
  Button,
  Card,
  ConfigProvider,
  Input,
  Result,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
} from "antd";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import useApi from "../../utils/useApi";
import { formatarTelefone } from "../ai-agent/textos";
import PainelDoContato, { juntarNomes } from "./PainelDoContato";

const { Text } = Typography;

export const FILTROS = [
  {
    id: "agente",
    rotulo: "Conversa com o agente",
    teste: (c) => c.ativo !== false && c.whatsapp,
  },
  {
    id: "relatorio",
    rotulo: "Recebe relatório",
    teste: (c) => c.ativo !== false && (c.rotinas || []).length > 0,
  },
  {
    id: "sem_filial",
    rotulo: "Sem filial",
    teste: (c) => (c.filiais || []).length === 0,
  },
  { id: "inativos", rotulo: "Inativos", teste: (c) => c.ativo === false },
];

export function filtrarContatos(contatos, { busca, filial, filtros }) {
  const termo = (busca || "").trim().toLowerCase();
  const digitos = termo.replace(/\D/g, "");
  const ativos = new Set(filtros || []);
  return (contatos || []).filter((contato) => {
    if (!ativos.has("inativos") && contato.ativo === false) return false;
    for (const filtro of FILTROS) {
      if (ativos.has(filtro.id) && !filtro.teste(contato)) return false;
    }
    if (filial && !(contato.filiais || []).includes(filial)) return false;
    if (!termo) return true;
    return (
      contato.nome.toLowerCase().includes(termo) ||
      (contato.usuario_email || "").toLowerCase().includes(termo) ||
      Boolean(digitos && String(contato.telefone).includes(digitos))
    );
  });
}

function numero(valor) {
  const convertido = Number(valor);
  return valor && Number.isInteger(convertido) ? convertido : undefined;
}

export function rotuloDoAgente(nomes) {
  if (nomes.length <= 2) {
    return { texto: `Agente em ${juntarNomes(nomes)}`, dica: "" };
  }
  return { texto: `Agente em ${nomes.length} filiais`, dica: nomes.join(", ") };
}

function textoDasFiliais(contato, totalDoCliente) {
  const nomes = contato.nomes_das_filiais || [];
  if (nomes.length === totalDoCliente && totalDoCliente > 1) {
    return "Todas as filiais";
  }
  return nomes.join(", ");
}

export default function Contatos() {
  const { user } = useAuth();
  const { getAgentCatalog, getAgentPhones } = useApi();
  const queryClient = useQueryClient();
  const podeVer = Boolean(user?.is_superuser);
  const [parametros, setParametros] = useSearchParams();
  const clienteId = numero(parametros.get("cliente"));
  const filialId = numero(parametros.get("filial"));
  const busca = parametros.get("busca") || "";
  const filtros = (parametros.get("filtros") || "").split(",").filter(Boolean);
  const pedido = parametros.get("contato");

  const catalogo = useQuery({
    queryKey: ["agent-catalog"],
    queryFn: getAgentCatalog,
    enabled: podeVer,
  });
  const telefones = useQuery({
    queryKey: ["agent-phones", "todos"],
    queryFn: () => getAgentPhones(),
    enabled: podeVer,
    staleTime: 0,
  });

  if (!podeVer) {
    return (
      <Result
        status="403"
        title="Acesso restrito"
        subTitle="Esta área é exclusiva para superadmins."
      />
    );
  }

  const mudar = (alteracoes, opcoes = {}) => {
    const proximos = new URLSearchParams(parametros);
    for (const [chave, valor] of Object.entries(alteracoes)) {
      if (valor === undefined || valor === null || valor === "") {
        proximos.delete(chave);
      } else {
        proximos.set(chave, String(valor));
      }
    }
    setParametros(proximos, opcoes);
  };

  const clientes = catalogo.data?.clientes || [];
  const cliente = clientes.find((c) => c.id === clienteId);
  const todasAsFiliais = catalogo.data?.filiais || [];
  const filiaisDe = (id) => todasAsFiliais.filter((f) => f.client_id === id);
  const nomeDoCliente = Object.fromEntries(clientes.map((c) => [c.id, c.name]));
  const filiais = cliente ? filiaisDe(cliente.id) : todasAsFiliais;
  const filialEscolhida = todasAsFiliais.find((f) => f.id === filialId);
  const todos = telefones.data?.telefones || [];
  const contatos = cliente
    ? todos.filter((c) => c.client === cliente.id)
    : todos;
  const lista = filtrarContatos(contatos, {
    busca,
    filial: filialId,
    filtros,
  });
  const aberto =
    pedido === "novo"
      ? null
      : todos.find((c) => String(c.id) === pedido) || null;
  const painelAberto = pedido === "novo" || Boolean(aberto);
  const clienteDoPainel = aberto
    ? clientes.find((c) => c.id === aberto.client)
    : cliente || clientes.find((c) => c.id === filialEscolhida?.client_id);

  const recarregar = () =>
    queryClient.invalidateQueries({ queryKey: ["agent-phones"] });

  const alternarFiltro = (id) =>
    mudar({
      filtros: filtros.includes(id)
        ? filtros.filter((f) => f !== id).join(",")
        : [...filtros, id].join(","),
    });

  const colunaDoCliente = {
    title: "Cliente",
    key: "cliente",
    render: (_, contato) => contato.cliente || nomeDoCliente[contato.client],
  };

  const colunas = [
    {
      title: "Contato",
      key: "contato",
      render: (_, contato) => (
        <span>
          <Text strong>{contato.nome}</Text>
          {contato.ativo === false && (
            <Tag style={{ marginLeft: 8 }}>Inativo</Tag>
          )}
          <Text type="secondary" style={{ display: "block", fontSize: 13 }}>
            {contato.usuario_email || "sem usuário"}
          </Text>
        </span>
      ),
    },
    {
      title: "Telefone",
      dataIndex: "telefone",
      render: (valor) => (
        <span style={{ whiteSpace: "nowrap" }}>{formatarTelefone(valor)}</span>
      ),
    },
    {
      title: "Filiais",
      key: "filiais",
      render: (_, contato) =>
        (contato.filiais || []).length ? (
          <span>
            {textoDasFiliais(contato, filiaisDe(contato.client).length)}
            {contato.filiais_pelo_usuario && (
              <Text type="secondary" style={{ display: "block", fontSize: 13 }}>
                do usuário no app
              </Text>
            )}
          </span>
        ) : (
          <Tag color="red">Nenhuma filial</Tag>
        ),
    },
    {
      title: "Agente",
      key: "agente",
      render: (_, contato) => {
        if (!(contato.filiais || []).length) {
          return <Text type="secondary">-</Text>;
        }
        if (!contato.whatsapp) return <Tag>Só relatórios</Tag>;
        const nomesDoAgente = contato.filiais_do_agente_nomes || [];
        if (!nomesDoAgente.length) {
          return <Tag color="red">Agente sem filial</Tag>;
        }
        const rotulo = rotuloDoAgente(nomesDoAgente);
        return (
          <span>
            <Tooltip title={rotulo.dica || undefined}>
              <Tag color="cyan">{rotulo.texto}</Tag>
            </Tooltip>
            <Text type="secondary" style={{ display: "block", fontSize: 13 }}>
              {contato.pode_escrever ? "pode registrar" : "só consulta"}
              {contato.hermes ? " · Hermes" : ""}
            </Text>
          </span>
        );
      },
    },
    {
      title: "Relatórios",
      key: "rotinas",
      render: (_, contato) =>
        (contato.rotinas || []).length ? (
          contato.rotinas.map((rotina) => (
            <span key={rotina.id} style={{ display: "block" }}>
              {rotina.name}
            </span>
          ))
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
  ];
  const colunasVisiveis = cliente
    ? colunas
    : [colunas[0], colunaDoCliente, ...colunas.slice(1)];

  const vazio = contatos.length ? (
    <span>
      Nenhum contato com esses filtros.{" "}
      <Button
        type="link"
        size="small"
        onClick={() =>
          mudar({
            cliente: undefined,
            busca: undefined,
            filial: undefined,
            filtros: undefined,
          })
        }>
        Limpar filtros
      </Button>
    </span>
  ) : cliente ? (
    "Este cliente ainda não tem contatos."
  ) : (
    "Nenhum contato cadastrado."
  );

  return (
    <ConfigProvider
      theme={{
        components: { Card: { headerBg: "var(--ouran-card-header-bg)" } },
      }}>
      <div
        style={{
          width: "100%",
          marginTop: 20,
          display: "flex",
          justifyContent: "center",
        }}>
        <div style={{ width: "94%" }}>
          <Card
            title="Contatos"
            extra={
              <Tooltip
                title={
                  clienteDoPainel || aberto
                    ? undefined
                    : "Escolha o cliente ou a filial para criar um contato."
                }>
                <Button
                  type="primary"
                  disabled={!(cliente || filialEscolhida)}
                  onClick={() => mudar({ contato: "novo" })}>
                  Novo contato
                </Button>
              </Tooltip>
            }>
            <Space wrap align="center" style={{ marginBottom: 12 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Todos os clientes"
                aria-label="Cliente"
                style={{ width: 240 }}
                value={cliente ? clienteId : undefined}
                options={clientes.map((c) => ({ value: c.id, label: c.name }))}
                onChange={(id) =>
                  mudar({
                    cliente: id,
                    filial:
                      id && filialEscolhida?.client_id !== id
                        ? undefined
                        : filialId,
                    contato: undefined,
                  })
                }
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Todas as filiais"
                aria-label="Filial"
                style={{ width: 260 }}
                value={filialEscolhida ? filialId : undefined}
                options={filiais.map((f) => ({
                  value: f.id,
                  label: cliente
                    ? f.name
                    : `${nomeDoCliente[f.client_id] || ""} · ${f.name}`,
                }))}
                onChange={(id) => mudar({ filial: id })}
              />
              <Input.Search
                allowClear
                placeholder="Buscar por nome, telefone ou e-mail"
                aria-label="Buscar contato"
                style={{ width: 300 }}
                value={busca}
                onChange={(e) =>
                  mudar({ busca: e.target.value }, { replace: true })
                }
              />
              {filialEscolhida && (
                <Text type="secondary">
                  {`${lista.length} ${lista.length === 1 ? "contato" : "contatos"} nesta filial, ${
                    lista.filter(
                      (c) =>
                        c.whatsapp &&
                        (c.filiais_do_agente || []).includes(filialId)
                    ).length
                  } conversam com o agente sobre ela.`}
                </Text>
              )}
            </Space>
            <Space
              wrap
              size={6}
              style={{ marginBottom: 14 }}
              role="group"
              aria-label="Filtros">
              {FILTROS.map((filtro) => {
                const ligado = filtros.includes(filtro.id);
                return (
                  <Button
                    key={filtro.id}
                    size="small"
                    shape="round"
                    type={ligado ? "primary" : "default"}
                    ghost={ligado}
                    aria-pressed={ligado}
                    onClick={() => alternarFiltro(filtro.id)}>
                    {filtro.rotulo}
                    <Text
                      type="secondary"
                      style={{ marginLeft: 4, color: "inherit" }}>
                      {contatos.filter(filtro.teste).length}
                    </Text>
                  </Button>
                );
              })}
            </Space>
            <Table
              size="middle"
              rowKey="id"
              loading={telefones.isFetching}
              columns={colunasVisiveis}
              dataSource={lista}
              pagination={false}
              scroll={{ x: true }}
              rowClassName={(contato) =>
                [
                  aberto?.id === contato.id ? "contato-aberto" : "",
                  contato.ativo === false ? "contato-inativo" : "",
                ]
                  .filter(Boolean)
                  .join(" ")
              }
              onRow={(contato) => ({
                tabIndex: 0,
                style: { cursor: "pointer" },
                onClick: () => mudar({ contato: contato.id }),
                onKeyDown: (e) => {
                  if (e.key === "Enter") mudar({ contato: contato.id });
                },
              })}
              locale={{ emptyText: vazio }}
            />
          </Card>
        </div>
      </div>
      {clienteDoPainel && (
        <PainelDoContato
          aberto={painelAberto}
          contato={aberto}
          cliente={clienteDoPainel}
          filiais={filiaisDe(clienteDoPainel.id)}
          contas={catalogo.data?.contas}
          filiaisIniciais={filialId ? [filialId] : []}
          contexto={`Cliente: ${clienteDoPainel.name}`}
          salvarRotulo="Criar contato"
          onFechar={() => mudar({ contato: undefined })}
          onSalvo={(_, mensagem) => {
            notification.success({ message: mensagem });
            mudar({ contato: undefined });
            recarregar();
          }}
          onExcluido={() => {
            notification.success({ message: "Contato excluído." });
            mudar({ contato: undefined });
            recarregar();
          }}
        />
      )}
    </ConfigProvider>
  );
}
